import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, TrendingUp, AlertCircle, RefreshCw, Sparkles, DollarSign, Clock, Ship, Factory, Flag, BarChart3 } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { ConversationalSidebar } from '../components/ConversationalSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TradeAnalyticsPage() {
  const [activeTab, setActiveTab] = useState('reports');
  const [morningReports, setMorningReports] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fleetComposition, setFleetComposition] = useState(null);
  const [capacityDistribution, setCapacityDistribution] = useState(null);
  const [builderStats, setBuilderStats] = useState(null);

  const fetchMorningReports = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trade-analytics/morning-reports`, {
        withCredentials: true
      });
      setMorningReports(response.data);
    } catch (error) {
      console.error('[TradeAnalytics] Error fetching morning reports:', error);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trade-analytics/opportunities`, {
        withCredentials: true
      });
      setOpportunities(response.data);
    } catch (error) {
      console.error('[TradeAnalytics] Error fetching opportunities:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trade-analytics/alerts`, {
        withCredentials: true
      });
      setAlerts(response.data);
    } catch (error) {
      console.error('[TradeAnalytics] Error fetching alerts:', error);
    }
  };


  const fetchFleetComposition = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trade-analytics/fleet/composition`, {
        withCredentials: true
      });
      setFleetComposition(response.data);
    } catch (error) {
      console.error('[TradeAnalytics] Error fetching fleet composition:', error);
    }
  };

  const fetchCapacityDistribution = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trade-analytics/fleet/capacity`, {
        withCredentials: true
      });
      setCapacityDistribution(response.data);
    } catch (error) {
      console.error('[TradeAnalytics] Error fetching capacity distribution:', error);
    }
  };

  const fetchBuilderStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trade-analytics/fleet/builders`, {
        withCredentials: true
      });
      setBuilderStats(response.data);
    } catch (error) {
      console.error('[TradeAnalytics] Error fetching builder stats:', error);
    }
  };

  useEffect(() => {
    fetchMorningReports();
    fetchOpportunities();
    fetchAlerts();
    if (activeTab === 'fleet') {
      fetchFleetComposition();
      fetchCapacityDistribution();
      fetchBuilderStats();
    }
  }, [activeTab]);

  const generateReport = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/trade-analytics/morning-reports/generate`, {}, {
        withCredentials: true
      });
      toast.success('Morning report generated successfully!');
      await fetchMorningReports();
    } catch (error) {
      console.error('[TradeAnalytics] Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const scanOpportunities = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/trade-analytics/opportunities/scan`, {}, {
        withCredentials: true
      });
      toast.success('Opportunity scan complete!');
      await fetchOpportunities();
    } catch (error) {
      console.error('[TradeAnalytics] Error scanning opportunities:', error);
      toast.error('Failed to scan opportunities');
    } finally {
      setLoading(false);
    }
  };

  const triggerSampleAlerts = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/trade-analytics/alerts/trigger`, {}, {
        withCredentials: true
      });
      toast.success('Sample alerts triggered!');
      await fetchAlerts();
    } catch (error) {
      console.error('[TradeAnalytics] Error triggering alerts:', error);
      toast.error('Failed to trigger alerts');
    } finally {
      setLoading(false);
    }
  };

  const markAlertRead = async (alertId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/trade-analytics/alerts/${alertId}/read`, {}, {
        withCredentials: true
      });
      await fetchAlerts();
    } catch (error) {
      console.error('[TradeAnalytics] Error marking alert read:', error);
    }
  };

  const getConfidenceBadgeColor = (score) => {
    if (score >= 80) return 'bg-[hsl(var(--success))] text-white';
    if (score >= 60) return 'bg-[hsl(var(--info))] text-white';
    return 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]';
  };

  return (
    <div className="space-y-8" data-testid="trade-analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
          Trade Analytics
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">
          Morning reports, opportunity radar, and sales alerts
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="reports" data-testid="tab-morning-reports">
            <FileText className="h-4 w-4 mr-2" />
            Morning Reports
          </TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="tab-opportunities">
            <TrendingUp className="h-4 w-4 mr-2" />
            Opportunity Radar
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertCircle className="h-4 w-4 mr-2" />
            Sales Alerts
          </TabsTrigger>
          <TabsTrigger value="fleet" data-testid="tab-fleet-analytics">
            <Ship className="h-4 w-4 mr-2" />
            Fleet Analytics
          </TabsTrigger>
        </TabsList>

        {/* Morning Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Daily lane analysis, fair-price bands, and top congestion moves
            </p>
            <Button
              onClick={generateReport}
              disabled={loading}
              data-testid="generate-report-button"
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>

          {morningReports.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <FileText className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No Reports Yet</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Click "Generate Report" to create your first morning market brief with lane analysis and insights.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {morningReports.map((report) => (
                <Card key={report.id} data-testid={`morning-report-${report.id}`}>
                  <CardHeader>
                    <CardTitle className="text-xl">{report.title}</CardTitle>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {new Date(report.report_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Summary */}
                    <div>
                      <h4 className="font-semibold mb-2">Executive Summary</h4>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">{report.summary}</p>
                    </div>

                    {/* Lane Analysis */}
                    <div>
                      <h4 className="font-semibold mb-3">Lane Analysis</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {report.lane_analysis.map((lane, idx) => (
                          <Card key={idx} className="bg-[hsl(var(--secondary))]" data-testid={`lane-${lane.lane}`}>
                            <CardContent className="pt-4">
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>{lane.lane}</span>
                                  <Badge className={getConfidenceBadgeColor(lane.confidence)}>
                                    {lane.confidence}% conf
                                  </Badge>
                                </div>
                                <div className="text-sm space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-[hsl(var(--muted-foreground))]">Fair Price:</span>
                                    <span className="font-medium" style={{ fontFamily: 'var(--font-mono)' }}>
                                      ${lane.fair_price_low} - ${lane.fair_price_high}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-[hsl(var(--muted-foreground))]">Current Rate:</span>
                                    <span className="font-semibold text-[hsl(var(--accent))]" style={{ fontFamily: 'var(--font-mono)' }}>
                                      ${lane.current_rate}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Congestion Moves */}
                    <div>
                      <h4 className="font-semibold mb-3">Top Congestion Moves</h4>
                      <div className="space-y-2">
                        {report.top_congestion_moves.map((move, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]" data-testid={`congestion-${move.port}`}>
                            <div>
                              <span className="font-medium">{move.port}</span>
                              <span className="text-sm text-[hsl(var(--muted-foreground))] ml-2">
                                {move.congestion_change}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline">{move.impact} Impact</Badge>
                              <span className="text-sm" style={{ fontFamily: 'var(--font-mono)' }}>
                                +{move.eta_delay_hours}h delay
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Weather Windows */}
                    <div>
                      <h4 className="font-semibold mb-3">Weather Windows</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {report.weather_windows.map((window, idx) => (
                          <div key={idx} className="p-3 rounded-lg border" data-testid={`weather-window-${idx}`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="font-medium block">{window.route}</span>
                                <span className="text-sm text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'var(--font-mono)' }}>
                                  {window.window_start} - {window.window_end}
                                </span>
                              </div>
                              <Badge variant="outline">Swell: {window.swell_height}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Opportunity Radar Tab */}
        <TabsContent value="opportunities" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Arbitrage detection with confidence scores and TCE upside
            </p>
            <Button
              onClick={scanOpportunities}
              disabled={loading}
              data-testid="scan-opportunities-button"
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {loading ? 'Scanning...' : 'Scan Opportunities'}
            </Button>
          </div>

          {opportunities.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <TrendingUp className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No Opportunities Found</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Click "Scan Opportunities" to analyze current market conditions and identify profitable trading opportunities.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {opportunities.map((opp) => (
                <Card key={opp.id} className="hover:shadow-md transition-shadow" data-testid={`opportunity-${opp.id}`}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                            {opp.lane} - {opp.commodity}
                          </h3>
                          <p className="text-sm text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'var(--font-mono)' }}>
                            {opp.origin} → {opp.destination}
                          </p>
                        </div>
                        <Badge className={getConfidenceBadgeColor(opp.confidence_score)} data-testid="confidence-badge">
                          {opp.confidence_score}% Confidence
                        </Badge>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-[hsl(var(--secondary))]">
                        <div>
                          <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] mb-1">
                            <DollarSign className="h-4 w-4" />
                            <span>Est. Profit</span>
                          </div>
                          <span className="text-xl font-semibold text-[hsl(var(--success))]" style={{ fontFamily: 'var(--font-mono)' }}>
                            ${(opp.estimated_profit_usd / 1000).toFixed(0)}K
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] mb-1">
                            <TrendingUp className="h-4 w-4" />
                            <span>TCE Upside</span>
                          </div>
                          <span className="text-xl font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
                            ${(opp.tce_upside_usd / 1000).toFixed(0)}K/day
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] mb-1">
                            <Clock className="h-4 w-4" />
                            <span>Laycan</span>
                          </div>
                          <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-mono)' }}>
                            {new Date(opp.laycan_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(opp.laycan_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      </div>

                      {/* Reasoning */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm">Why This Works:</h4>
                        <ul className="space-y-1">
                          {opp.reasoning.map((reason, idx) => (
                            <li key={idx} className="text-sm text-[hsl(var(--muted-foreground))] flex items-start gap-2">
                              <span className="text-[hsl(var(--accent))] mt-0.5">•</span>
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Badge variant="outline">{opp.vessel_class}</Badge>
                        <Badge variant="outline" className="capitalize">{opp.opportunity_type}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Sales Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Threshold-based notifications for lanes and commodities
            </p>
            <Button
              onClick={triggerSampleAlerts}
              disabled={loading}
              data-testid="trigger-alerts-button"
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              {loading ? 'Triggering...' : 'Trigger Sample Alerts'}
            </Button>
          </div>

          {alerts.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <AlertCircle className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No Alerts</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Click "Trigger Sample Alerts" to see how threshold-based notifications work.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <Card
                  key={alert.id}
                  className={`${!alert.is_read ? 'border-[hsl(var(--accent))] border-l-4' : ''}`}
                  data-testid={`alert-${alert.id}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="capitalize">
                            {alert.alert_type.replace('_', ' ')}
                          </Badge>
                          {!alert.is_read && (
                            <Badge className="bg-[hsl(var(--accent))] text-white">New</Badge>
                          )}
                        </div>
                        <p className="text-sm">{alert.message}</p>
                        <div className="flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
                          {alert.lane && (
                            <span style={{ fontFamily: 'var(--font-mono)' }}>Lane: {alert.lane}</span>
                          )}
                          {alert.commodity && (
                            <span>Commodity: {alert.commodity}</span>
                          )}
                          <span>
                            {new Date(alert.triggered_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      {!alert.is_read && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => markAlertRead(alert.id)}
                          data-testid="mark-read-button"
                        >
                          Mark Read
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Fleet Analytics Tab */}
        <TabsContent value="fleet" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Global Fleet Intelligence</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Powered by Clarksons World Fleet Register • 12,709 dry cargo vessels
              </p>
            </div>
          </div>

          {/* Flag Composition */}
          {fleetComposition && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="h-5 w-5" />
                  Top Flags & Vessel Types
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold mb-3">Top 10 Flag States</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={fleetComposition.top_flags}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="flag" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))' 
                        }}
                      />
                      <Bar dataKey="count" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-3">Vessel Type Distribution</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {fleetComposition.vessel_types.slice(0, 6).map((type, idx) => (
                      <Card key={idx}>
                        <CardContent className="p-4">
                          <div className="text-sm font-medium">{type.type}</div>
                          <div className="text-2xl font-bold text-[hsl(var(--primary))]">{type.count}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            Avg {(type.avg_dwt / 1000).toFixed(0)}k DWT
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-3">Fleet Age Distribution</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={fleetComposition.age_distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))' 
                        }}
                      />
                      <Bar dataKey="count" fill="hsl(var(--accent))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Capacity Distribution */}
          {capacityDistribution && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  DWT Capacity Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={capacityDistribution.distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="label" 
                      stroke="hsl(var(--muted-foreground))"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))' 
                      }}
                    />
                    <Legend />
                    <Bar dataKey="count" fill="hsl(var(--primary))" name="Vessel Count" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Builder & Owner Statistics */}
          {builderStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Factory className="h-5 w-5" />
                    Top 10 Shipbuilders
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {builderStats.top_builders.map((builder, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-[hsl(var(--muted))] rounded-lg">
                        <div>
                          <div className="font-medium">{builder.builder}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {builder.vessel_count} vessels • {(builder.total_dwt / 1000000).toFixed(1)}M DWT
                          </div>
                        </div>
                        <Badge>{idx + 1}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ship className="h-5 w-5" />
                    Top 10 Fleet Owners
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {builderStats.top_owners.map((owner, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-[hsl(var(--muted))] rounded-lg">
                        <div>
                          <div className="font-medium">{owner.owner}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {owner.vessel_count} vessels • {(owner.total_dwt / 1000000).toFixed(1)}M DWT
                          </div>
                        </div>
                        <Badge>{idx + 1}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!fleetComposition && !capacityDistribution && !builderStats && (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <Ship className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto animate-pulse" />
                  <p className="text-[hsl(var(--muted-foreground))]">Loading fleet analytics...</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

      </Tabs>

      {/* AI Conversational Sidebar */}
      <ConversationalSidebar 
        module="trade_analytics"
        context={{
          reports: morningReports,
          opportunities: opportunities,
          alerts: alerts
        }}
        onAction={(type, params) => {
          console.log('[Trade Analytics] AI Action:', type, params);
          // Actions can be implemented: generate report, view opportunity, etc.
        }}
      />
    </div>
  );
}
